import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, X, Warning } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'spam', label: 'Spam or misleading' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'fake', label: 'Fake / fraudulent' },
  { value: 'copyright', label: 'Copyright violation' },
  { value: 'harassment', label: 'Harassment or hateful' },
  { value: 'other', label: 'Other' },
];

/**
 * ReportButton — small flag icon button that opens a modal to report UGC.
 *
 * props:
 *   targetType: 'vendor' | 'listing' | 'photo' | 'theme' | 'package'
 *   targetId: string
 *   targetName?: string (for snapshot in the report)
 *   variant?: 'icon' | 'text' (default 'icon')
 *   size?: number (default 14)
 *   testId?: string
 */
const ReportButton = ({ targetType, targetId, targetName, variant = 'icon', size = 14, testId }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Don't show report button if user is not logged in (anonymous can't report)
  if (!user) return null;
  // Don't allow self-reporting
  if (user.id === targetId) return null;

  const submit = async () => {
    if (!category) {
      toast.error('Please select a reason');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/reports`, {
        target_type: targetType,
        target_id: targetId,
        target_name: targetName || '',
        category,
        description: description.trim(),
      }, { withCredentials: true });
      toast.success('Report submitted. Our team will review it within 48 hours.');
      setOpen(false);
      setCategory('');
      setDescription('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const triggerTestId = testId || `report-${targetType}-${targetId}`;
  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
          aria-label="Report this content"
          title="Report this content"
          className="inline-flex items-center justify-center w-7 h-7 rounded-full text-text-secondary hover:bg-red-50 hover:text-red-600 transition-colors"
          data-testid={triggerTestId}
        >
          <Flag size={size} />
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
          className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-red-600 transition-colors"
          data-testid={triggerTestId}
        >
          <Flag size={size} /> Report
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !submitting) setOpen(false); }}
          data-testid={`report-modal-${targetId}`}
        >
          <div className="bg-white max-w-md w-full border border-red-300 shadow-2xl rounded">
            <div className="px-5 py-4 border-b border-[#E8E1D5] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Flag size={20} className="text-red-600" weight="duotone" />
                <h3 className="font-heading text-xl font-medium">Report Content</h3>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => !submitting && setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-sand-100 text-text-secondary"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {targetName && (
                <div className="text-sm text-text-secondary">
                  Reporting: <span className="font-medium text-text-primary">{targetName}</span>
                </div>
              )}
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary block mb-2">Reason *</label>
                <div className="space-y-1">
                  {CATEGORIES.map((c) => (
                    <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer p-2 hover:bg-sand-100 rounded">
                      <input
                        type="radio"
                        name="report-category"
                        value={c.value}
                        checked={category === c.value}
                        onChange={(e) => setCategory(e.target.value)}
                        className="accent-red-600"
                        data-testid={`report-category-${c.value}`}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary block mb-1">Details (optional)</label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add any additional context to help us review..."
                  maxLength={1000}
                  className="w-full px-3 py-2 border border-[#E8E1D5] rounded-none text-sm focus:outline-none focus:border-brand-primary"
                  data-testid="report-description"
                />
              </div>
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900">
                <Warning size={16} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <span>False reports may result in your account being suspended. Reports are reviewed by our moderation team within 48 hours.</span>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={submit}
                  disabled={submitting || !category}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="report-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </Button>
                <Button
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  variant="outline"
                  className="rounded-none border-[#E8E1D5]"
                  data-testid="report-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportButton;
